<template>
  <div>
    <div v-if="discountsLoading || sorting">
      <div class="text-center px-10 py-10">
        <v-progress-circular
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
    </div>
    <div v-else>
      <div v-if="discountsList.length">
        <hb-data-table-header>
          <template v-slot:description>
            Arrange the order of merchandise discounts by importance or desirability. Drag and drop <hb-icon class="vertical-align-middle" style="position:relative;top:3px;">mdi-drag-custom</hb-icon> each row.
          </template>
          <template v-slot:actions>
            <hb-btn color="secondary" small @click="editSettings = true">Merchandise Discount Settings</hb-btn>
          </template>
        </hb-data-table-header>
        <hb-data-table
          :headers="headers"
          :items="discountsList"
        >
          <template v-slot:body="props">
            <draggable
              :list="props.items"
              tag="tbody"
              @end="saveSortOrder(props.items)"
            >
              <tr v-for="(item, index) in props.items" :key="index">
                <td>
                  <hb-btn icon tooltip="Drag" active-state-off>
                    mdi-drag-custom
                  </hb-btn>
                </td>
                <td class="capitalize">{{ item.name }}</td>
                <td class="capitalize">{{ item.description }}</td>
                <td class="capitalize">{{ item.type }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </draggable>
          </template>
        </hb-data-table>
      </div>
      <hb-empty-state
        v-else 
        :message="'Add merchandise discounts from promotions and discounts library'"
        class="mt-2"
      >
      </hb-empty-state>
    </div>
    <hb-modal
      v-model="editSettings"
      size="medium"
      title='Merchandise Discount Settings'
      @close="editSettings = false"
    >
      <template v-slot:subheader>
        {{ dialogDescription }}
      </template>
      <template v-slot:content>
        <v-form class="promo-form" ref="form">
          <hb-form
            label="Quantity"
            description="Count quantity for all products while applying discount"
            full
          >
            <template v-slot:tooltipBody> {{ toolTipMessage('moq') }} </template>
            <HbSwitch
              v-model="countTotalQtyForMOQ"
              :label="countTotalQtyForMOQ ? 'Enabled' : 'Disabled'"
              @change="saveSettings('countTotalQtyForMOQ')"
              :disabled="saving || !hasManagePromotionPermission"
            />
          </hb-form>
          <hb-form
            label="Multiple Discounts"
            description="Multiple discounts can be applied on same merchandise"
            full
          >
            <template v-slot:tooltipBody> {{ toolTipMessage('multiple') }} </template>
            <HbSwitch
              v-model="allowMultipleDiscountsOnSameMerchandise"
              :label="allowMultipleDiscountsOnSameMerchandise ? 'Enabled' : 'Disabled'"
              @change="saveSettings('allowMultipleDiscountsOnSameMerchandise')"
              :disabled="saving || !hasManagePromotionPermission"
            />
          </hb-form>
        </v-form>
      </template>
    </hb-modal>
  </div>
</template>

<script type="text/babel">
import { mapGetters, mapActions } from "vuex";
import { EventBus } from "../../../EventBus.js";
import api from "../../../assets/api.js";
import Draggable from "vuedraggable";
import { notificationMixin } from "@/mixins/notificationMixin.js";

export default {
  name: "MerchandiseDiscountManagement",
  data() {
    return {
      headers: [
        { text: "", value: "drag", align: "left", sortable: false, width: 10 },
        { text: "Name", value: "name", sortable: false },
        {
          text: "Description",
          value: "description",
          sortable: false
        },
        { text: "Type", value: "type", sortable: false },
        { text: "Value", value: "value", sortable: false },
      ],
      editSettings: false,
      discountsList: [],
      discountsLoading: false,
      sorting: false,
      saving: false,
      countTotalQtyForMOQ: false,
      allowMultipleDiscountsOnSameMerchandise: false,
      settingsCache: {},
      dialogDescription: 'Manage merchandise discount settings'
    };
  },
  async created(){
    await this.fetchData()
    await this.fetchSettings()
  },
  mixins: [notificationMixin],
  props: ['permissions'],
  components: {
    Draggable
  },
  computed: {
    ...mapGetters({
      hasPermission: 'authenticationStore/rolePermission',
    }),
    hasManagePromotionPermission() {
      return this.permissions?.manage_merchandise_discounts ?? false;
    },
  },
  methods: {
    async saveSortOrder(items) {
      if(!this.hasManagePromotionPermission){
        this.showMessageNotification({type: 'error', description: 'You do not have permission to change this'});
        return;
      }
      this.sorting = true;
      try{
        let index = 0;
        let updated = items.map(i => {
          index += 1;
          return{
            id: i.id,
            name: i.name,
            sort_order: index
          }
        });
      
        await api.post(this, api.MERCHANDISE_DISCOUNT + '/update-sort-order', updated);
        await this.fetchData();
        this.showMessageNotification({type: 'success', description: 'Sort order updated successfully'});
      } catch (err) {
        console.log("Error while updating sort order => ", err);
        this.showMessageNotification({type: 'error', description: 'Failed to update sort order'});
      } finally {
        this.sorting = false;
      }
    },
    async fetchData(){
      //fetch merchandise discounts
      this.discountsLoading = true;
      try{
        let r = await api.get(this, api.MERCHANDISE_DISCOUNT + '?sorted=true');
        this.discountsList = r.merchandise_discounts;
      } catch(err){
        console.log("Error while fetching merchandise discounts => ", err);
        this.showMessageNotification({type: 'error', description: 'Failed to fetch merchandise discounts'});
      } finally {
        this.discountsLoading = false;
      }
    },
    async saveSettings(field){
      this.saving = true;
      try{
        let formData = {
          settings : { [field]: this[field]  },
        };
        await api.post(this, api.SETTINGS + 'merchandiseDiscount', formData);
        this.showMessageNotification({type: 'success', description: 'Merchandise discount setting updated successfully'});
      } catch(err){
        console.log('Error while saving merchandise discount setting => ', err);
        this.showMessageNotification({type: 'error', description: 'Failed to update merchandise discount setting'})
        this[field] = this.settingsCache[field];
      } finally {
        this.saving = false;
      }
    },
    async fetchSettings(){
      //fetch toggle settings
      const r = await api.get(this, api.SETTINGS_MERCHANDISE_DISCOUNT);
      this.allowMultipleDiscountsOnSameMerchandise = Number(r?.settings?.allowMultipleDiscountsOnSameMerchandise) ? true : false;
      this.countTotalQtyForMOQ = Number(r?.settings?.countTotalQtyForMOQ) ? true : false;
      this.settingsCache = {allowMultipleDiscountsOnSameMerchandise: this.allowMultipleDiscountsOnSameMerchandise, countTotalQtyForMOQ: this.countTotalQtyForMOQ};
    },
    toolTipMessage(label){
      if(!this.hasManagePromotionPermission) return 'You do not have permission to change this';
      if(label === 'moq') return 'If OFF, counts quantity of merchandise not yet discounted';
      else return 'If OFF, applies discount on merchandise not yet discounted';
    }
  }
};
</script>

<style scoped>
.v-list-item {
  min-height: 34px;
}
.v-list-item__title {
  min-width: 110px;
}
.align-items-center {
  align-items: center;
}
.vertical-align-middle {
  vertical-align: middle;
}
.border-bottom-dashed {
  border-bottom: 1px dashed;
}
.capitalize {
  text-transform: capitalize;
}

.text-lower-case {
  text-transform: lowercase;
}

.list-item-height {
  min-height: 40px;
}
</style>
